import { useEffect } from 'react';
import {
  CheckCircle2,
  AlertCircle,
  AlertTriangle,
  Info,
  X,
  Check,
  Trash2,
  HelpCircle,
} from 'lucide-react';
import { useFeedbackStore, ToastItem, ToastType } from '../stores/feedbackStore';

const toastConfig: Record<
  ToastType,
  {
    icon: typeof CheckCircle2;
    border: string;
    bg: string;
    iconColor: string;
    defaultTitle: string;
  }
> = {
  success: {
    icon: CheckCircle2,
    border: 'border-emerald-500/40 dark:border-emerald-500/30',
    bg: 'bg-white/95 dark:bg-[#0e1626]/95',
    iconColor: 'text-emerald-500',
    defaultTitle: 'Thành công',
  },
  error: {
    icon: AlertCircle,
    border: 'border-rose-500/40 dark:border-rose-500/30',
    bg: 'bg-white/95 dark:bg-[#0e1626]/95',
    iconColor: 'text-rose-500',
    defaultTitle: 'Lỗi',
  },
  warning: {
    icon: AlertTriangle,
    border: 'border-amber-500/40 dark:border-amber-500/30',
    bg: 'bg-white/95 dark:bg-[#0e1626]/95',
    iconColor: 'text-amber-500',
    defaultTitle: 'Cảnh báo',
  },
  info: {
    icon: Info,
    border: 'border-sky-500/40 dark:border-sky-500/30',
    bg: 'bg-white/95 dark:bg-[#0e1626]/95',
    iconColor: 'text-sky-500',
    defaultTitle: 'Thông tin',
  },
};

function ToastCard({ item, onDismiss }: { item: ToastItem; onDismiss: () => void }) {
  const cfg = toastConfig[item.type] || toastConfig.info;
  const Icon = cfg.icon;

  return (
    <div
      role="status"
      className={`pointer-events-auto flex items-start gap-3 p-3.5 rounded-xl shadow-lg border backdrop-blur-md transition-all animate-slide-in max-w-sm w-full ${cfg.bg} ${cfg.border}`}
    >
      <Icon className={`w-5 h-5 flex-shrink-0 mt-0.5 ${cfg.iconColor}`} />
      <div className="flex-1 min-w-0 pr-1">
        {item.title && (
          <h4 className="text-xs font-semibold text-slate-900 dark:text-white leading-tight mb-0.5">
            {item.title}
          </h4>
        )}
        <p className="text-xs text-slate-600 dark:text-slate-300 font-normal leading-relaxed break-words">
          {item.message}
        </p>
      </div>
      <button
        onClick={onDismiss}
        className="p-1 -mr-1 -mt-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
        title="Đóng thông báo"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

export default function FeedbackOverlay() {
  const { toasts, removeToast, confirmState, closeConfirm } = useFeedbackStore();

  // Handle ESC key to cancel confirm dialog
  useEffect(() => {
    if (!confirmState.isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        closeConfirm(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [confirmState.isOpen, closeConfirm]);

  const { isOpen, options } = confirmState;
  const isDanger = options.type !== 'primary';
  const ConfirmIcon = isDanger ? AlertTriangle : HelpCircle;

  return (
    <>
      {/* Toast notifications container (Floating at top-right corner) */}
      <div
        aria-live="polite"
        className="fixed top-4 right-4 z-[9999] pointer-events-none flex flex-col gap-2.5 max-w-md w-full items-end px-3 sm:px-0"
      >
        {toasts.map((item) => (
          <ToastCard key={item.id} item={item} onDismiss={() => removeToast(item.id)} />
        ))}
      </div>

      {/* Confirm Dialog Modal */}
      {isOpen && (
        <div
          className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-slate-950/70 backdrop-blur-sm animate-fade-in"
          onClick={() => closeConfirm(false)}
        >
          <div
            className="w-full max-w-md rounded-2xl bg-white dark:bg-[#0f172a] border border-slate-200 dark:border-white/10 shadow-2xl p-5 sm:p-6 space-y-4 animate-scale-up"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start gap-3.5">
              <div
                className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                  isDanger
                    ? 'bg-rose-500/15 text-rose-600 dark:text-rose-400 border border-rose-500/20'
                    : 'bg-sky-500/15 text-sky-600 dark:text-sky-400 border border-sky-500/20'
                }`}
              >
                <ConfirmIcon className="w-5 h-5" />
              </div>

              <div className="flex-1 min-w-0">
                <h3 className="text-sm sm:text-base font-semibold text-slate-900 dark:text-white">
                  {options.title || (isDanger ? 'Xác nhận thực hiện' : 'Xác nhận')}
                </h3>
                <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-300 font-normal mt-1 leading-relaxed">
                  {options.message}
                </p>
              </div>
            </div>

            <div className="flex items-center justify-end gap-2.5 pt-2 border-t border-slate-100 dark:border-white/5">
              <button
                type="button"
                onClick={() => closeConfirm(false)}
                className="btn-outline text-xs h-8 px-3 font-normal inline-flex items-center gap-1.5"
              >
                <X className="w-3.5 h-3.5" />
                <span>{options.cancelText || 'Hủy'}</span>
              </button>

              <button
                type="button"
                onClick={() => closeConfirm(true)}
                className={`text-xs h-8 px-3.5 font-medium inline-flex items-center gap-1.5 rounded-lg shadow-sm transition-all text-white ${
                  isDanger
                    ? 'bg-rose-600 hover:bg-rose-700 active:bg-rose-800'
                    : 'bg-sky-600 hover:bg-sky-700 active:bg-sky-800'
                }`}
              >
                {isDanger ? <Trash2 className="w-3.5 h-3.5" /> : <Check className="w-3.5 h-3.5" />}
                <span>{options.confirmText || 'Xác nhận'}</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
